import { Box, FormikSelectInputNative, SelectInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';

import { ProgramType } from 'types';

import { percentageFormatter } from '../../../../helpers/percentageFormatter';
import { useGetPriceSheetsQuery, useLazyGetPriceSheetDetailsQuery } from '../../../../services/pricing';

export interface EnergyBackupPricingFormValues {
  priceSheet: string;
  escalationRateMin: string;
  escalationRateMax: string;
  minTerm: string;
  maxTerm: string;
  minSystemCost: string;
  maxSystemCost: string;
}

export const BulkEditEnergyBackupPricingFormSection = () => {
  const { setFieldValue, isSubmitting, values, touched, errors, handleBlur } =
    useFormikContext<EnergyBackupPricingFormValues>();
  const { priceSheet } = values;
  const { data: priceSheetResults } = useGetPriceSheetsQuery({
    pageNum: 1,
    pageSize: 1000,
    programType: ProgramType.energyBackup,
  });
  const [trigger, { data: priceSheetDetails = {} }] = useLazyGetPriceSheetDetailsQuery();

  useEffect(() => {
    if (priceSheetResults?.data?.length && priceSheet) {
      trigger({ id: priceSheet });
    }
  }, [priceSheet, priceSheetResults?.data, trigger]);

  const priceSheetOptions = useMemo(
    () =>
      priceSheetResults?.data?.map((priceSheet) => ({
        label: priceSheet.name,
        value: priceSheet.id,
      })) ?? [],
    [priceSheetResults?.data],
  );

  const escRateOptions = useMemo(() => {
    const escRates = new Set<number>();
    priceSheetDetails?.prices?.forEach((p: any) => {
      if (p.escalationRate != null) escRates.add(p.escalationRate);
    });
    return Array.from(escRates)
      .toSorted((a, b) => a - b)
      .map((rate: number) => ({ label: percentageFormatter.format(rate), value: rate }));
  }, [priceSheetDetails?.prices]);

  const termOptions = useMemo(() => {
    const terms = new Set<number>();
    priceSheetDetails?.prices?.forEach((p: any) => {
      if (p.term != null) terms.add(p.term);
    });
    return Array.from(terms)
      .toSorted((a, b) => a - b)
      .map((term: number) => ({ label: `${term} months`, value: term }));
  }, [priceSheetDetails?.prices]);

  const systemCostOptions = useMemo(() => {
    const costs = new Set<number>();
    priceSheetDetails?.prices?.forEach((p: any) => {
      if (p.minSystemCost != null) costs.add(p.minSystemCost);
      if (p.maxSystemCost != null) costs.add(p.maxSystemCost);
    });
    return Array.from(costs)
      .toSorted((a, b) => a - b)
      .map((cost: number) => ({ label: `$${cost.toLocaleString()}`, value: cost }));
  }, [priceSheetDetails?.prices]);

  const handlePriceSheetMappingSelection = useCallback(
    (event: any) => {
      const id = event?.target?.value?.value ?? '';
      setFieldValue('priceSheet', id);
      setFieldValue('escalationRateMin', '');
      setFieldValue('escalationRateMax', '');
      setFieldValue('minTerm', '');
      setFieldValue('maxTerm', '');
      setFieldValue('minSystemCost', '');
      setFieldValue('maxSystemCost', '');
      if (id) {
        trigger({ id });
      }
    },
    [setFieldValue, trigger],
  );

  const selectedPriceSheetOption = useMemo(
    () => priceSheetOptions.find((option: { value: string }) => option.value === priceSheet) ?? null,
    [priceSheetOptions, priceSheet],
  );

  return (
    <Box childGap="lg">
      <SelectInput
        id="priceSheet"
        name="priceSheet"
        label="Price Sheet"
        value={selectedPriceSheetOption}
        options={priceSheetOptions}
        onChange={handlePriceSheetMappingSelection}
        onBlur={handleBlur}
        isDisabled={isSubmitting}
        error={touched.priceSheet && (errors.priceSheet as string)}
        isRequired
      />
      <Box direction="row" childGap="lg">
        <Field
          name="escalationRateMin"
          id="escalationRateMin"
          label="Escalator Min"
          component={FormikSelectInputNative}
          options={escRateOptions}
          isDisabled={!escRateOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="escalationRateMax"
          id="escalationRateMax"
          label="Escalator Max"
          component={FormikSelectInputNative}
          options={escRateOptions}
          isDisabled={!escRateOptions.length || isSubmitting}
          isRequired
        />
      </Box>
      <Box direction="row" childGap="lg">
        <Field
          name="minTerm"
          id="minTerm"
          label="Min Term"
          component={FormikSelectInputNative}
          options={termOptions}
          isDisabled={!termOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="maxTerm"
          id="maxTerm"
          label="Max Term"
          component={FormikSelectInputNative}
          options={termOptions}
          isDisabled={!termOptions.length || isSubmitting}
          isRequired
        />
      </Box>
      <Box direction="row" childGap="lg">
        <Field
          name="minSystemCost"
          id="minSystemCost"
          label="Min System Cost"
          component={FormikSelectInputNative}
          options={systemCostOptions}
          isDisabled={!systemCostOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="maxSystemCost"
          id="maxSystemCost"
          label="Max System Cost"
          component={FormikSelectInputNative}
          options={systemCostOptions}
          isDisabled={!systemCostOptions.length || isSubmitting}
          isRequired
        />
      </Box>
    </Box>
  );
};
